import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CulsightLoader from "../components/ui/CulsightLoader";
import {
  getCampaigns,
  getEmailTemplates,
  getUserGroups,
  getDepartments,
  getLandingPages,
  getSmtpConfigs,
  getTwilioConfigs,
  getVoices,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  executeCampaign,
  pauseCampaign,
  resumeCampaign,
  bulkDeleteCampaigns,
  bulkExportCampaigns,
} from "../api/CampaignsApi";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Plus, Search, BarChart3, Trash2, Edit,
  Mail, MessageSquare, Phone, PhoneCall, Target, Calendar, Play, Pause,
  MoreVertical, Download, CheckSquare, Clock, Rocket, Sparkles
} from "lucide-react";
import { toast } from "sonner";

const CHANNEL_META = {
  email: { label: "Email", icon: Mail, color: "#8c3add", accent: "bg-[#8c3add]/10 text-[#8c3add]" },
  sms: { label: "SMS", icon: MessageSquare, color: "#1fa7a1", accent: "bg-[#1fa7a1]/10 text-[#1fa7a1]" },
  whatsapp: { label: "WhatsApp", icon: Phone, color: "#22c55e", accent: "bg-[#22c55e]/10 text-[#22c55e]" },
  voice: { label: "Voice", icon: PhoneCall, color: "#f59e0b", accent: "bg-[#f59e0b]/10 text-[#f59e0b]" },
};

// Channels jinme landing page select kar sakte hain
const LANDING_PAGE_CHANNELS = ["email", "whatsapp"];

const emptyForm = (channel = "email") => ({
  name: "",
  simulation_type: channel,
  smtp_config_id: "",
  twilio_config_id: "",
  user_group: "",
  email_template_id: "",
  landing_page_id: "",
  user_count: 0,
  scheduled_at: "",
  target_departments: [],
  voice_id: "",
  voice_script: "",
  elevenlabs_agent_id: "",
});
const SEARCH_KEY = "search";

const isValidChannel = (c) => Object.keys(CHANNEL_META).includes(c);
const getErrorMessage = (err, fallback) => {
  const data = err.response?.data;
  const detail = data?.detail ?? data?.message ?? data?.error;

  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "";
        return field ? `${field}: ${d.msg}` : d.msg || JSON.stringify(d);
      })
      .join(", ");
  }

  if (detail) return JSON.stringify(detail);
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const fieldErrors = Object.entries(data)
      .map(([field, msgs]) => {
        const text = Array.isArray(msgs) ? msgs.join(", ") : msgs;
        return field === "non_field_errors" ? text : `${field}: ${text}`;
      })
      .join(" | ");
    if (fieldErrors) return fieldErrors;
  }

  return err.message || fallback;
};
const getOpenedCount = (campaign, chan) => {
  if (chan === "whatsapp") {
    return campaign.whatsapp_opened ?? campaign.messages_opened ?? campaign.emails_opened ?? 0;
  }
  return campaign.emails_opened ?? 0;
};

const Campaigns = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const channelParam = searchParams.get("channel");
  const channel = isValidChannel(channelParam) ? channelParam : "email";

  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [smtpConfigs, setSmtpConfigs] = useState([]);
  const [twilioConfigs, setTwilioConfigs] = useState([]);
  const [voices, setVoices] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedOnce = useRef(false);
  const requestIdRef = useRef(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [executingId, setExecutingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState(emptyForm(channel));
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [showLaunchDialog, setShowLaunchDialog] = useState(false);
  const [campaignToLaunch, setCampaignToLaunch] = useState(null);

  // Tab badalne par URL update
  const handleChannelChange = (v) => {
    setSearchParams({ channel: v }, { replace: true });
    setStatusFilter("all");
    setSearch("");
    setDebouncedSearch("");
    setSelectedCampaigns([]);
  };

  // Typing ke 400ms baad hi search backend ko jayega
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [channel, statusFilter, debouncedSearch]);

  const fetchData = async () => {
    const myRequestId = ++requestIdRef.current;

    if (!hasLoadedOnce.current) setInitialLoading(true);
    else setRefreshing(true);

    const startTime = Date.now();
    try {
      const [campaignsRes, templatesRes, groupsRes, deptsRes, lpRes, smtpRes, twRes, voicesRes] =
        await Promise.all([
          getCampaigns(
            statusFilter,
            channel,
            debouncedSearch ? { [SEARCH_KEY]: debouncedSearch } : {}
          ),
          getEmailTemplates(),
          getUserGroups(),
          getDepartments(),
          getLandingPages(),
          getSmtpConfigs(),
          getTwilioConfigs(),
          getVoices(),
        ]);
      const responseData = campaignsRes.data || {};

      if (myRequestId === requestIdRef.current) {
        setCampaigns(responseData.campaigns || []);
        setSummary(responseData.summary?.[0] || null);
        setTemplates(Array.isArray(templatesRes) ? templatesRes : templatesRes?.results || templatesRes?.data || []);
        setUserGroups(Array.isArray(groupsRes) ? groupsRes : groupsRes?.results || groupsRes?.data || []);
        setDepartments(Array.isArray(deptsRes) ? deptsRes : deptsRes?.results || deptsRes?.data || []);
        setLandingPages(Array.isArray(lpRes) ? lpRes : lpRes?.results || lpRes?.data || []);
        setSmtpConfigs(Array.isArray(smtpRes) ? smtpRes : smtpRes?.results || smtpRes?.data || []);
        setTwilioConfigs(Array.isArray(twRes) ? twRes : twRes?.results || twRes?.data || []);
        setVoices(Array.isArray(voicesRes) ? voicesRes : voicesRes?.results || voicesRes?.data || []);
      }
    } catch (error) {
      if (myRequestId === requestIdRef.current) {
        toast.error(getErrorMessage(error, "Failed to load campaigns data"));
      }
    }

    const elapsed = Date.now() - startTime;
    const minDelay = 300;
    if (elapsed < minDelay) {
      await new Promise((resolve) => setTimeout(resolve, minDelay - elapsed));
    }

    if (myRequestId === requestIdRef.current) {
      hasLoadedOnce.current = true;
      setInitialLoading(false);
      setRefreshing(false);
    }
  };

  const openCreate = () => { setFormData(emptyForm(channel)); setIsDialogOpen(true); };

  // Frontend validation nahi hai: Create dabate hi API hit hoti hai, error backend ke response se dikhta hai
  const handleCreate = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const payload = {
        name: formData.name,
        simulation_type: formData.simulation_type,

        smtp_config_id:
          formData.simulation_type === "email" ? formData.smtp_config_id : "",

        smtp: "",

        user_group_id: formData.user_group,

        email_template_id: formData.email_template_id,

        // Landing page ab email aur whatsapp dono ke liye jayega
        landing_page_id:
          LANDING_PAGE_CHANNELS.includes(formData.simulation_type) &&
          formData.landing_page_id &&
          formData.landing_page_id !== "none"
            ? formData.landing_page_id
            : "",

        user_count: formData.user_count,

        scheduled_at: formData.scheduled_at || null,

        target_departments: formData.target_departments,

        twilio_config_id:
          formData.twilio_config_id === "mock" ? "" : formData.twilio_config_id,

        voice_id:
          formData.simulation_type === "voice" ? formData.voice_id : "",

        voice_script:
          formData.simulation_type === "voice" ? formData.voice_script : "",

        elevenlabs_agent_id:
          formData.simulation_type === "voice" ? formData.elevenlabs_agent_id : "",
      };

      // EDIT
      if (editingCampaign) {
        await updateCampaign(editingCampaign.id, payload);

        toast.success("Campaign updated successfully");
      }

      // CREATE
      else {
        await createCampaign(payload);

        toast.success(
          `${CHANNEL_META[formData.simulation_type].label} campaign created`
        );
      }

      setIsDialogOpen(false);
      setEditingCampaign(null);
      setFormData(emptyForm(channel));

      fetchData();

    } catch (error) {
      console.error("Campaign save error:", error);
      toast.error(getErrorMessage(error, "Failed to save campaign"));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (campaign) => {
    setEditingCampaign(campaign);

    setFormData({
      name: campaign.name || "",
      simulation_type: campaign.simulation_type || "email",
      smtp_config_id: campaign.smtp_config_id || "",
      twilio_config_id: campaign.twilio_config_id || "",
      user_group: campaign.user_group_id || campaign.user_group || "",
      email_template_id: campaign.email_template_id || "",
      landing_page_id: campaign.landing_page_id || "",
      user_count: campaign.user_count || 0,

      // datetime-local ke liye local IST value
      scheduled_at: campaign.scheduled_at
        ? new Date(campaign.scheduled_at).toLocaleString("sv-SE", {
          timeZone: "Asia/Kolkata",
        }).slice(0, 16)
        : "",

      target_departments: Array.isArray(campaign.target_departments)
        ? campaign.target_departments
        : [],

      voice_id: campaign.voice_id || "",
      voice_script: campaign.voice_script || "",
      elevenlabs_agent_id: campaign.elevenlabs_agent_id || "",
    });

    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!campaignToDelete) return;

    try {
      setDeleting(true);

      await deleteCampaign(campaignToDelete.id);

      toast.success("Campaign deleted");
      setShowDeleteDialog(false);
      setCampaignToDelete(null);

      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete campaign"));
    } finally {
      setDeleting(false);
    }
  };

  // Ye button click pe sirf dialog kholega
  const openLaunchDialog = (campaign) => {
    setCampaignToLaunch(campaign);
    setShowLaunchDialog(true);
  };

  // Ye actual launch dialog ke "Confirm" button pe chalega
  const handleExecute = async () => {
    if (!campaignToLaunch) return;
    setExecutingId(campaignToLaunch.id);
    try {
      await executeCampaign(campaignToLaunch.id);
      toast.success("Campaign launched — sending in progress");
      setShowLaunchDialog(false);
      setCampaignToLaunch(null);
      setTimeout(fetchData, 1500);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to launch campaign"));
    }
    setExecutingId(null);
  };

  const handlePause = async (id) => {
    try { await pauseCampaign(id); toast.success("Paused"); fetchData(); }
    catch (error) { toast.error(getErrorMessage(error, "Failed to pause")); }
  };

  const handleResume = async (id) => {
    try { await resumeCampaign(id); toast.success("Resumed"); fetchData(); }
    catch (error) { toast.error(getErrorMessage(error, "Failed to resume")); }
  };

  const handleSelectAll = (checked) => {
    if (checked) setSelectedCampaigns(filteredCampaigns.map((c) => c.id));
    else setSelectedCampaigns([]);
  };

  const handleSelectCampaign = (id, checked) => {
    if (checked) setSelectedCampaigns([...selectedCampaigns, id]);
    else setSelectedCampaigns(selectedCampaigns.filter((cid) => cid !== id));
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteCampaigns(selectedCampaigns);
      toast.success(`Deleted ${selectedCampaigns.length} campaigns`);
      setSelectedCampaigns([]);
      setShowBulkDeleteDialog(false);
      fetchData();
    } catch (error) { toast.error(getErrorMessage(error, "Failed to delete campaigns")); }
  };

  const handleBulkExport = async () => {
    try {
      const response = await bulkExportCampaigns(selectedCampaigns);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url; link.setAttribute("download", "campaigns_export.csv"); document.body.appendChild(link); link.click(); link.remove();
      toast.success("Export downloaded");
    } catch (error) { toast.error(getErrorMessage(error, "Export failed")); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-[#1fa7a1] text-white";
      case "sending": return "bg-[#8c3add] text-white animate-pulse";
      case "scheduled": return "bg-[#8c3add] text-white";
      case "paused": return "bg-amber-500 text-white";
      case "completed": return "bg-gray-400 text-white";
      case "draft": return "bg-gray-200 text-gray-700";
      default: return "bg-gray-100 text-gray-600";
    }
  };


  const filteredCampaigns = campaigns;

  // Templates filtered for the form's selected simulation type
  const filteredTemplates = useMemo(
    () => templates.filter((t) => (t.simulation_type || "email") === formData.simulation_type),
    [templates, formData.simulation_type]
  );

  const channelStats = (chan) => {
    return summary?.channels?.[chan] || 0;
  };

  if (initialLoading) {
    return (
      <CulsightLoader
        variant="page"
        label="Culsight"
      />
    );
  }

  const ActiveIcon = CHANNEL_META[channel].icon;
  const channelColor = CHANNEL_META[channel].color;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">Campaigns</h1>
          <p className="text-gray-500 text-sm mt-1">Run phishing simulations across Email, SMS, and WhatsApp</p>
        </div>
        <Button
          onClick={openCreate}
          className="gap-2 text-white"
          style={{ backgroundColor: channelColor }}
          data-testid="create-campaign-btn"
        >
          <Plus size={18} />
          New {CHANNEL_META[channel].label} Campaign
        </Button>
      </div>

      {/* Channel Tabs */}
      <Tabs value={channel} onValueChange={handleChannelChange}>
        <TabsList className="grid grid-cols-4 max-w-2xl">
          {Object.entries(CHANNEL_META).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <TabsTrigger key={key} value={key} data-testid={`channel-tab-${key}`} className="gap-2">
                <Icon size={16} />
                {meta.label}
                <Badge className="bg-gray-100 text-gray-600 ml-1">{channelStats(key)}</Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {[
          {
            label: "Total",
            value: summary?.total_campaigns || 0,
            icon: Target,
            color: channelColor,
          },
          {
            label: "Active",
            value: summary?.status?.active || 0,
            icon: Play,
            color: "#1fa7a1",
          },
          {
            label: "Scheduled",
            value: summary?.status?.scheduled || 0,
            icon: Clock,
            color: "#8c3add",
          },
          {
            label: "Paused",
            value: summary?.status?.paused || 0,
            icon: Pause,
            color: "#f59e0b",
          },
          {
            label: "Completed",
            value: summary?.status?.completed || 0,
            icon: CheckSquare,
            color: "#64748b",
          },
        ].map((stat, index) => (
          <Card
            key={index}
            className={`border shadow-sm cursor-pointer transition-all ${statusFilter === (stat.label === "Total" ? "all" : stat.label.toLowerCase())
              ? "ring-2"
              : "border-gray-100"
              }`}
            style={
              statusFilter === (stat.label === "Total" ? "all" : stat.label.toLowerCase())
                ? {
                  borderColor: channelColor,
                  "--tw-ring-color": `${channelColor}33`,
                }
                : {}
            }
            onClick={() =>
              setStatusFilter(
                stat.label === "Total" ? "all" : stat.label.toLowerCase()
              )
            }
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div
                className="p-2 rounded-xl"
                style={{ backgroundColor: `${stat.color}15` }}
              >
                <stat.icon size={18} style={{ color: stat.color }} />
              </div>

              <div>
                <p className="text-xl font-bold text-[#1f1f23]">
                  {stat.value}
                </p>
                <p className="text-xs text-gray-500">
                  {stat.label}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}

      </div>

      {/* Bulk Actions Bar */}
      {selectedCampaigns.length > 0 && (
        <div className="flex items-center justify-between p-4 border rounded-xl" style={{ backgroundColor: `${channelColor}10`, borderColor: `${channelColor}33` }}>
          <span className="text-sm font-medium" style={{ color: channelColor }}>
            {selectedCampaigns.length} campaign{selectedCampaigns.length > 1 ? "s" : ""} selected
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleBulkExport} className="gap-2">
              <Download size={16} /> Export CSV
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setShowBulkDeleteDialog(true)} className="gap-2">
              <Trash2 size={16} /> Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input placeholder={`Search ${CHANNEL_META[channel].label.toLowerCase()} campaigns...`} className="pl-10" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="campaigns-search" />
        </div>
      </div>

      {/* Table */}
      <Card className="border border-gray-100 shadow-sm relative" data-testid="campaigns-table">
        {refreshing && (
          <CulsightLoader
            variant="overlay"
            label="Culsight"
          />
        )}
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="py-3 px-4 w-10">
                    <Checkbox checked={selectedCampaigns.length === filteredCampaigns.length && filteredCampaigns.length > 0} onCheckedChange={handleSelectAll} />
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">User Group</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Template</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Stats</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-gray-500">
                      <ActiveIcon size={28} className="mx-auto mb-2 text-gray-300" />
                      No {CHANNEL_META[channel].label} campaigns yet — click &ldquo;New {CHANNEL_META[channel].label} Campaign&rdquo; to create one.
                    </td>
                  </tr>
                ) : (
                  filteredCampaigns.map((campaign) => {
                    const canExecute = ["draft", "scheduled", "paused"].includes(campaign.status);
                    return (
                      <tr key={campaign.id} className="border-b border-gray-50 hover:bg-gray-50/40 transition-colors" data-testid={`campaign-row-${campaign.id}`}>
                        <td className="py-4 px-4">
                          <Checkbox checked={selectedCampaigns.includes(campaign.id)} onCheckedChange={(checked) => handleSelectCampaign(campaign.id, checked)} />
                        </td>
                        <td className="py-4 px-4">
                          <div className="cursor-pointer hover:text-[#8c3add]" onClick={() => navigate(`/campaigns/${campaign.id}`)} data-testid={`campaign-name-${campaign.id}`}>
                            <p className="font-medium text-[#1f1f23] hover:text-[#8c3add] transition-colors">{campaign.name}</p>
                            {campaign.scheduled_at && (
                              <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                                <Calendar size={12} /> {new Date(campaign.scheduled_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-gray-600">{campaign.user_group}</td>
                        <td className="py-4 px-4 text-gray-600">{campaign.email_template_name || "—"}</td>
                        <td className="py-4 px-4">
                          <div className="flex flex-nowrap items-center gap-1.5 text-xs">
                            <span className={`px-2 py-0.5 rounded-full whitespace-nowrap ${CHANNEL_META[channel].accent}`}>{campaign.emails_sent} sent</span>
                            {(channel === "email" || channel === "whatsapp") && (
                              <span className="px-2 py-0.5 rounded-full whitespace-nowrap bg-[#1fa7a1]/10 text-[#1fa7a1]">
                                {getOpenedCount(campaign, channel)} opened
                              </span>
                            )}
                            <span className="px-2 py-0.5 rounded-full whitespace-nowrap bg-[#f59e0b]/10 text-[#f59e0b]">{campaign.links_clicked} clicked</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={`${getStatusColor(campaign.status)} gap-1`}>
                            {campaign.status?.charAt(0).toUpperCase() + campaign.status?.slice(1)}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-1">
                            {canExecute && (
                              <Button
                                size="sm"
                                className="gap-1 text-white h-8"
                                style={{ backgroundColor: channelColor }}
                                onClick={() => openLaunchDialog(campaign)}
                                disabled={executingId === campaign.id}
                                data-testid={`launch-${campaign.id}`}
                              >
                                {executingId === campaign.id ? (
                                  <>
                                    <CulsightLoader variant="inline" />
                                    Launching...
                                  </>
                                ) : (
                                  <>
                                    <Rocket size={14} />
                                    Launch
                                  </>
                                )}
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="p-2"><MoreVertical size={16} /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem className="gap-2" onClick={() => navigate(`/campaigns/${campaign.id}`)}><BarChart3 size={14} /> View Details</DropdownMenuItem>
                                <DropdownMenuItem
                                  className="gap-2"
                                  onClick={() => openEdit(campaign)}
                                >
                                  <Edit size={14} /> Edit
                                </DropdownMenuItem>
                                {campaign.status === "active" && (
                                  <DropdownMenuItem className="gap-2" onClick={() => handlePause(campaign.id)}>
                                    <Pause size={14} /> Pause
                                  </DropdownMenuItem>
                                )}
                                {campaign.status === "paused" && (
                                  <DropdownMenuItem className="gap-2" onClick={() => handleResume(campaign.id)}>
                                    <Play size={14} /> Resume
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="gap-2 text-red-600 focus:text-red-600"
                                  onClick={() => {
                                    setCampaignToDelete(campaign);
                                    setShowDeleteDialog(true);
                                  }}
                                >
                                  <Trash2 size={14} /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Campaign Dialog (adaptive by channel) */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);

          if (!open) {
            setEditingCampaign(null);
            setFormData(emptyForm(channel));
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="campaign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ActiveIcon size={18} style={{ color: channelColor }} />
              {editingCampaign ? "Edit" : "New"}{" "}
              {CHANNEL_META[formData.simulation_type].label} Campaign
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} noValidate className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Campaign Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Q1 Phishing Drill" data-testid="campaign-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Simulation Channel</Label>
                <Select
                  value={formData.simulation_type}
                  onValueChange={(v) =>
                    setFormData({
                      ...formData,
                      simulation_type: v,
                      email_template_id: "",
                      // email <-> whatsapp me landing page bani rahe, sms/voice pe clear
                      landing_page_id: LANDING_PAGE_CHANNELS.includes(v) ? formData.landing_page_id : "",
                    })
                  }
                >
                  <SelectTrigger data-testid="campaign-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">📧 Email (SMTP)</SelectItem>
                    <SelectItem value="sms">💬 SMS (Twilio)</SelectItem>
                    <SelectItem value="whatsapp">📱 WhatsApp (Interakt)</SelectItem>
                    <SelectItem value="voice">📞 Voice / Vishing (Twilio + ElevenLabs)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Channel-specific provider selectors */}
            {formData.simulation_type === "email" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>SMTP Configuration</Label>
                  <Select value={formData.smtp_config_id} onValueChange={(v) => setFormData({ ...formData, smtp_config_id: v })}>
                    <SelectTrigger data-testid="campaign-smtp-select"><SelectValue placeholder={smtpConfigs.length === 0 ? "No SMTP configured – go to Settings" : "Select SMTP"} /></SelectTrigger>
                    <SelectContent>
                      {smtpConfigs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.host})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Landing Page</Label>
                  <Select value={formData.landing_page_id} onValueChange={(v) => setFormData({ ...formData, landing_page_id: v })}>
                    <SelectTrigger data-testid="campaign-landing-select"><SelectValue placeholder="Optional – select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {landingPages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {(formData.simulation_type === "sms" || formData.simulation_type === "whatsapp" || formData.simulation_type === "voice") && (
              <div className={formData.simulation_type === "whatsapp" ? "grid grid-cols-2 gap-3" : ""}>
              <div className="space-y-2">
                <Label>Twilio Configuration</Label>
                <Select value={formData.twilio_config_id} onValueChange={(v) => setFormData({ ...formData, twilio_config_id: v })}>
                  <SelectTrigger data-testid="campaign-twilio-select">
                    <SelectValue placeholder={twilioConfigs.length === 0 ? "No Twilio configured – will run in MOCK mode" : "Select Twilio"} />
                  </SelectTrigger>
                  <SelectContent>
                    {twilioConfigs.length === 0 && <SelectItem value="mock">🧪 Mock mode (no live send)</SelectItem>}
                    {twilioConfigs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {twilioConfigs.length === 0 && (
                  <p className="text-xs text-amber-600">
                    ⚠ Add Twilio credentials in <strong>Settings → Twilio</strong> to send real messages. Right now this campaign will mock-send.
                  </p>
                )}
              </div>

              {/* WhatsApp landing page selector (Twilio ke saath same row me) */}
              {formData.simulation_type === "whatsapp" && (
              <div className="space-y-2">
                <Label>Landing Page</Label>
                <Select
                  value={formData.landing_page_id}
                  onValueChange={(v) => setFormData({ ...formData, landing_page_id: v })}
                >
                  <SelectTrigger data-testid="campaign-whatsapp-landing-select">
                    <SelectValue placeholder="Optional – select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {landingPages.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
              </div>
            )}

            {formData.simulation_type === "voice" && (
              <>
                <div className="space-y-2">
                  <Label>Voice</Label>
                  <Select value={formData.voice_id} onValueChange={(v) => setFormData({ ...formData, voice_id: v })}>
                    <SelectTrigger data-testid="campaign-voice-select">
                      <SelectValue placeholder="Choose a voice (or leave blank for Twilio built-in)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="twilio_polly">Twilio Polly (default, no ElevenLabs)</SelectItem>
                      {voices.map((v) => (
                        <SelectItem key={v.voice_id} value={v.voice_id}>
                          {v.name} {v.mocked ? "(mock)" : v.category === "cloned" ? "(cloned)" : "(premade)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Clone your own voice or add ElevenLabs credentials in <strong>Settings → Voice Studio</strong>.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Call Script (played to recipient — supports {"{{name}}"})</Label>
                  <textarea
                    className="w-full min-h-[110px] rounded-md border border-input bg-transparent px-3 py-2 text-sm font-mono"
                    value={formData.voice_script}
                    onChange={(e) => setFormData({ ...formData, voice_script: e.target.value })}
                    placeholder="Hello {{name}}, this is IT Security. We detected suspicious activity on your account. Press 1 to speak with an agent."
                    data-testid="campaign-voice-script"
                  />
                </div>
                <div className="space-y-2 p-3 border border-purple-200 bg-purple-50/40 rounded-lg">
                  <Label className="flex items-center gap-2 text-[#8c3add]">
                    <Sparkles size={14} /> Real-time Adaptive AI (ElevenLabs Conversational AI)
                  </Label>
                  <Input
                    value={formData.elevenlabs_agent_id}
                    onChange={(e) => setFormData({ ...formData, elevenlabs_agent_id: e.target.value })}
                    placeholder="Optional — paste ElevenLabs Agent ID (leave blank for static script)"
                    data-testid="campaign-agent-id"
                  />
                  <p className="text-[11px] text-gray-500">
                    When set, Twilio streams the call to ElevenLabs Conversational AI so the voice <strong>adapts to the recipient&rsquo;s replies in real-time</strong>. Requires <code>ELEVENLABS_API_KEY</code> in backend/.env and an Agent configured at <a href="https://elevenlabs.io/app/conversational-ai" target="_blank" rel="noreferrer" className="underline">elevenlabs.io/app/conversational-ai</a>.
                  </p>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>User Group</Label>
                <Select value={formData.user_group} onValueChange={(v) => setFormData({ ...formData, user_group: v })}>
                  <SelectTrigger data-testid="campaign-group-select"><SelectValue placeholder="Select group" /></SelectTrigger>
                  <SelectContent>
                    {userGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} ({g.member_count || 0})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Template ({CHANNEL_META[formData.simulation_type].label})</Label>
                <Select value={formData.email_template_id} onValueChange={(v) => setFormData({ ...formData, email_template_id: v })}>
                  <SelectTrigger data-testid="campaign-template-select">
                    <SelectValue placeholder={filteredTemplates.length === 0 ? `No ${formData.simulation_type} templates yet — clone from Template Library or use AI Studio` : "Select template"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* <div className="grid grid-cols-2 gap-3"> */}
              {/* <div className="space-y-2">
                <Label>Target Users (estimate)</Label>
                <Input type="number" value={formData.user_count} onChange={(e) => setFormData({ ...formData, user_count: parseInt(e.target.value) || 0 })} placeholder="50" data-testid="campaign-count-input" />
              </div> */}
              <div className="space-y-2">
                <Label>Schedule (Optional)</Label>
                <Input type="datetime-local" value={formData.scheduled_at} onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })} data-testid="campaign-schedule-input" />
              </div>
            {/* </div> */}

            <div className="space-y-2">
              <Label>Target Departments (Optional)</Label>
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg max-h-32 overflow-y-auto">
                {departments.map((dept) => (
                  <label key={dept.id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.target_departments.includes(dept.name)}
                      onCheckedChange={(checked) => {
                        if (checked) setFormData({ ...formData, target_departments: [...formData.target_departments, dept.name] });
                        else setFormData({ ...formData, target_departments: formData.target_departments.filter((d) => d !== dept.name) });
                      }}
                    />
                    <span className="text-sm">{dept.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                className="text-white"
                style={{ backgroundColor: CHANNEL_META[formData.simulation_type].color }}
                disabled={saving}
                data-testid="campaign-submit-btn"
              >
                {saving ? (
                  <>
                    <CulsightLoader variant="inline" />
                    {editingCampaign ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  editingCampaign
                    ? "Update Campaign"
                    : formData.scheduled_at
                      ? "Schedule Campaign"
                      : "Create Campaign"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Single Delete Confirmation */}
      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);

          if (!open) {
            setCampaignToDelete(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>

          <div className="py-2">
            <p className="text-gray-600">
              Are you sure you want to delete{" "}
              <strong>{campaignToDelete?.name}</strong>?
            </p>

            <p className="text-sm text-gray-400 mt-2">
              This action cannot be undone.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setCampaignToDelete(null);
              }}
            >
              Cancel
            </Button>

            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <CulsightLoader variant="inline" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Delete Campaign
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm Bulk Delete</DialogTitle></DialogHeader>
          <p className="text-gray-600">
            Are you sure you want to delete <strong>{selectedCampaigns.length}</strong> campaign{selectedCampaigns.length > 1 ? "s" : ""}? This cannot be undone.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete}>
              Delete {selectedCampaigns.length} Campaign{selectedCampaigns.length > 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Launch Confirmation */}
      <Dialog
        open={showLaunchDialog}
        onOpenChange={(open) => {
          setShowLaunchDialog(open);
          if (!open) setCampaignToLaunch(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket size={18} className="text-[#8c3add]" />
              Launch Campaign
            </DialogTitle>
          </DialogHeader>

          <div className="py-2">
            <p className="text-gray-600">
              Launch <strong>{campaignToLaunch?.name}</strong> now? This will send
              messages to all targeted users.
            </p>
            <p className="text-sm text-gray-400 mt-2">
              This action cannot be undone.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowLaunchDialog(false);
                setCampaignToLaunch(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="text-white"
              style={{ backgroundColor: channelColor }}
              onClick={handleExecute}
              disabled={executingId === campaignToLaunch?.id}
            >
              {executingId === campaignToLaunch?.id ? (
                <>
                  <CulsightLoader variant="inline" />
                  Launching...
                </>
              ) : (
                <>
                  <Rocket size={16} className="mr-2" />
                  Launch Campaign
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Campaigns;